/**
 * Spectrum Wishlist Controller — spectrum-ai canonical variant (BLT)
 *
 * Heart toggle for PDP buttons, product-card buttons, and the header badge.
 *   - Guests: single-list toggle backed by localStorage (SDK guest-aware).
 *   - Logged-in: multi-list picker popover (create lists, add/remove per list).
 *
 * Dependencies:
 *   - spectrum-sdk.js  (provides window.Spectrum.wishlist + Spectrum.events)
 *   - App embed block  (provides window.__spectrumAi.customer)
 *
 * Cross-component sync rides Spectrum.events (wishlist:added / :removed /
 * :merged) which the SDK emits from its own mutation methods — so the header
 * count and every matching heart stay in sync no matter which surface mutated.
 */
(function() {
  'use strict';
  if (window.__spectrumWlInit) return;
  window.__spectrumWlInit = true;

  var GUEST_KEY = '__spectrum_wishlist_guest';

  /* THEME-SPECIFIC: Header badge selector */
  var HEADER_BADGE_SELECTOR = '#spectrum-wl-header-count';
  /* END THEME-SPECIFIC */

  /* ── In-flight guard ───────────────────────────────────────────────────── */
  var _inflight = {};

  /* Wishlists cached from the last getWishlists() call so the picker opens
     instantly; invalidated whenever a wishlist mutation event fires. */
  var cachedWishlists = null;

  /* ── Helpers ────────────────────────────────────────────────────────────── */

  function extractLists(resultData) {
    if (!resultData) return [];
    if (Array.isArray(resultData.wishlists)) return resultData.wishlists;
    if (resultData.data && Array.isArray(resultData.data.wishlists)) return resultData.data.wishlists;
    if (Array.isArray(resultData)) return resultData;
    return [];
  }

  function _currency() {
    return (window.Spectrum && window.Spectrum.getActiveCurrency && window.Spectrum.getActiveCurrency()) || '';
  }

  function getLib() {
    var lib = (window.Spectrum && window.Spectrum.wishlist)
           || (window.__spectrumAi && window.__spectrumAi.lib && window.__spectrumAi.lib.wishlist);
    return lib && typeof lib.getWishlists === 'function' ? lib : null;
  }

  function isCustomerLoggedIn() {
    return !!(window.__spectrumAi && window.__spectrumAi.customer);
  }

  function waitForLib(callback, maxRetries) {
    var retries = 0;
    maxRetries = maxRetries || 15;
    function check() {
      var lib = getLib();
      if (lib) {
        callback(lib);
      } else if (retries < maxRetries) {
        retries++;
        setTimeout(check, 200);
      }
    }
    check();
  }

  function getGuestGids() {
    try {
      var raw = localStorage.getItem(GUEST_KEY);
      if (!raw) return [];
      var parsed = JSON.parse(raw);
      if (!Array.isArray(parsed.items)) return [];
      var seen = {};
      return parsed.items
        .map(function(i) { return i.shopifyProductGid; })
        .filter(function(gid) {
          if (!gid || seen[gid]) return false;
          seen[gid] = true;
          return true;
        });
    } catch(e) { return []; }
  }

  function escapeHtml(str) {
    if (!str) return '';
    var div = document.createElement('div');
    div.appendChild(document.createTextNode(str));
    return div.innerHTML;
  }

  /** Build the add payload from a wrapper's data attributes. */
  function buildAddData(gid, variantGid, handle, title, image, price) {
    return {
      shopifyProductGid: gid,
      shopifyVariantGid: variantGid || null,
      handle: handle || '',
      title: title || '',
      image: image || '',
      metadata: price ? { priceAtAdd: String(price), currencyCode: _currency() } : undefined
    };
  }

  /* ── Header Count Badge ──────────────────────────────────────────────── */

  /* THEME-SPECIFIC: Header badge update */
  function updateHeaderCount(count) {
    var el = document.querySelector(HEADER_BADGE_SELECTOR);
    if (!el) return;
    el.textContent = count;
    if (count > 0) {
      el.removeAttribute('hidden');
    } else {
      el.setAttribute('hidden', '');
    }
  }
  /* END THEME-SPECIFIC */

  /* ── Wishlist State Sync — hydrates is-wishlisted on buttons ────────── */

  function syncWishlistState() {
    if (!isCustomerLoggedIn()) return;
    waitForLib(function(lib) {
      lib.getWishlists({ limit: 100 }).then(function(result) {
        if (!result.ok) return;
        var lists = extractLists(result.data);
        cachedWishlists = lists;
        var wishedGids = {};
        var itemCountFallback = 0;
        lists.forEach(function(l) {
          if (Array.isArray(l.items)) {
            l.items.forEach(function(item) {
              if (item.shopifyProductGid) wishedGids[item.shopifyProductGid] = true;
            });
          } else if (typeof l.itemCount === 'number') {
            itemCountFallback += l.itemCount;
          }
        });
        applyWishlistButtons(document, wishedGids);
        var gidCount = Object.keys(wishedGids).length;
        updateHeaderCount(gidCount || itemCountFallback);
      });
    });
  }

  function applyWishlistButtons(root, wishedGids) {
    root.querySelectorAll('[data-spectrum-wishlist-btn]').forEach(function(el) {
      var gid = el.dataset.productGid;
      if (!gid) return;
      el.classList.toggle('is-wishlisted', !!wishedGids[gid]);
    });
  }

  /* ── Guest State Hydration ─────────────────────────────────────────── */

  function hydrateGuestState() {
    if (isCustomerLoggedIn()) return;
    var gids = getGuestGids();
    var gidSet = {};
    gids.forEach(function(gid) { gidSet[gid] = true; });
    document.querySelectorAll('[data-spectrum-wishlist-btn]').forEach(function(el) {
      if (gidSet[el.dataset.productGid]) {
        el.classList.add('is-wishlisted');
      }
    });
    updateHeaderCount(gids.length);
  }

  /* ── Multi-List Picker (logged-in) ─────────────────────────────────── */

  var pickerEl = null;
  var pickerBackdrop = null;
  var pickerAnchor = null;
  var pickerGid = null;
  var pickerVariantGid = null;
  var pickerHandle = null;
  var pickerTitle = null;
  var pickerImage = null;
  var pickerPrice = null;
  var pickerLists = [];

  function isProductInAnyList() {
    return pickerLists.some(function(list) {
      return Array.isArray(list.items) && list.items.some(function(i) {
        return i.shopifyProductGid === pickerGid;
      });
    });
  }

  function ensurePickerDOM() {
    if (pickerEl) return;

    pickerBackdrop = document.createElement('div');
    pickerBackdrop.className = 'spectrum-wl-picker-backdrop';
    document.body.appendChild(pickerBackdrop);

    pickerEl = document.createElement('div');
    pickerEl.className = 'spectrum-wl-picker';
    pickerEl.setAttribute('role', 'dialog');
    pickerEl.setAttribute('aria-label', 'Save to wishlist');
    pickerEl.innerHTML =
      '<div class="spectrum-wl-picker__header">Save to list</div>' +
      '<div class="spectrum-wl-picker__lists"></div>' +
      '<div class="spectrum-wl-picker__create">' +
        '<input type="text" class="spectrum-wl-picker__input" placeholder="New list name…" maxlength="50" aria-label="New list name">' +
        '<button type="button" class="spectrum-wl-picker__create-btn" aria-label="Create list">+</button>' +
      '</div>';
    document.body.appendChild(pickerEl);

    pickerEl.addEventListener('change', function(e) {
      if (e.target.type === 'checkbox' && e.target.dataset.listId) {
        handleCheckboxChange(e.target);
      }
    });

    pickerEl.querySelector('.spectrum-wl-picker__create-btn').addEventListener('click', handleCreateList);

    pickerEl.querySelector('.spectrum-wl-picker__input').addEventListener('keydown', function(e) {
      if (e.key === 'Enter') { e.preventDefault(); handleCreateList(); }
    });

    pickerEl.addEventListener('click', function(e) { e.stopPropagation(); });

    pickerBackdrop.addEventListener('click', closePicker);
  }

  function openPicker(anchor, lib, gid, variantGid, handle, title, image, price) {
    ensurePickerDOM();

    if (pickerEl.classList.contains('is-open') && pickerAnchor === anchor) {
      closePicker();
      return;
    }

    if (pickerEl.classList.contains('is-open')) {
      closePicker();
    }

    pickerAnchor = anchor;
    pickerGid = gid;
    pickerVariantGid = variantGid;
    pickerHandle = handle;
    pickerTitle = title;
    pickerImage = image;
    pickerPrice = price;
    pickerLists = [];

    pickerEl.querySelector('.spectrum-wl-picker__input').value = '';

    positionPicker(anchor);
    pickerEl.classList.add('is-open');
    pickerBackdrop.classList.add('is-open');

    /* Use cached data if available; only fetch from API when there's no cache */
    if (cachedWishlists) {
      pickerLists = cachedWishlists;
      renderPickerLists(pickerLists, gid);
    } else {
      var listsContainer = pickerEl.querySelector('.spectrum-wl-picker__lists');
      listsContainer.innerHTML = '<div class="spectrum-wl-picker__loading"><div class="spectrum-wl-picker__spinner"></div></div>';
      lib.getWishlists({ limit: 100 }).then(function(result) {
        if (!pickerEl.classList.contains('is-open') || pickerGid !== gid) return;
        pickerLists = extractLists(result.data);
        cachedWishlists = pickerLists;
        renderPickerLists(pickerLists, gid);
      });
    }
  }

  function positionPicker(anchor) {
    if (window.innerWidth < 750) {
      pickerEl.style.top = '';
      pickerEl.style.left = '';
      pickerEl.style.position = '';
      return;
    }

    var rect = anchor.getBoundingClientRect();
    var scrollY = window.scrollY || document.documentElement.scrollTop;
    var scrollX = window.scrollX || document.documentElement.scrollLeft;
    var pickerWidth = 256;
    var vpWidth = document.documentElement.clientWidth;

    var top = rect.bottom + scrollY + 8;
    var left = rect.left + scrollX + (rect.width / 2) - (pickerWidth / 2);
    if (left < 8) left = 8;
    if (left + pickerWidth > vpWidth - 8) left = vpWidth - pickerWidth - 8;

    pickerEl.style.position = 'absolute';
    pickerEl.style.top = top + 'px';
    pickerEl.style.left = left + 'px';
  }

  function renderPickerLists(lists, gid) {
    var container = pickerEl.querySelector('.spectrum-wl-picker__lists');
    if (!lists.length) {
      container.innerHTML = '<div class="spectrum-wl-picker__empty">No lists yet. Create one below.</div>';
      return;
    }

    var html = '';
    lists.forEach(function(list) {
      var isInList = Array.isArray(list.items) && list.items.some(function(i) {
        return i.shopifyProductGid === gid;
      });
      var count = list.itemCount != null ? list.itemCount : (list.items ? list.items.length : 0);
      html += '<label class="spectrum-wl-picker__item">' +
        '<input type="checkbox" data-list-id="' + escapeHtml(list.id) + '"' + (isInList ? ' checked' : '') + '>' +
        '<span class="spectrum-wl-picker__name">' + escapeHtml(list.name || 'Unnamed') + ' (' + count + ')</span>' +
        '</label>';
    });
    container.innerHTML = html;
  }

  function closePicker() {
    if (!pickerEl || !pickerEl.classList.contains('is-open')) return;
    pickerEl.classList.remove('is-open');
    pickerBackdrop.classList.remove('is-open');

    if (pickerAnchor && pickerLists.length) {
      var inAny = isProductInAnyList();
      document.querySelectorAll('[data-spectrum-wishlist-btn][data-product-gid="' + pickerGid + '"]').forEach(function(el) {
        el.classList.toggle('is-wishlisted', inAny);
      });
    }

    pickerAnchor = null;
  }

  function setItemLoading(checkbox, active) {
    var label = checkbox.closest('.spectrum-wl-picker__item');
    if (!label) return;
    if (active) {
      var spinner = document.createElement('span');
      spinner.className = 'spectrum-wl-picker__item-spinner';
      label.appendChild(spinner);
    } else {
      var existing = label.querySelector('.spectrum-wl-picker__item-spinner');
      if (existing) existing.remove();
    }
  }

  function handleCheckboxChange(checkbox) {
    var listId = checkbox.dataset.listId;
    var isChecked = checkbox.checked;
    var lib = getLib();
    if (!lib) return;

    checkbox.disabled = true;
    setItemLoading(checkbox, true);

    if (isChecked) {
      lib.addItem(listId, buildAddData(pickerGid, pickerVariantGid, pickerHandle, pickerTitle, pickerImage, pickerPrice)).then(function(result) {
        checkbox.disabled = false;
        setItemLoading(checkbox, false);
        if (!result.ok) { checkbox.checked = false; return; }

        var list = pickerLists.find(function(l) { return l.id === listId; });
        if (list && Array.isArray(list.items)) {
          var newItem = { shopifyProductGid: pickerGid, shopifyVariantGid: pickerVariantGid, handle: pickerHandle };
          var rd = result.data;
          if (rd) newItem.id = rd.id || (rd.data && rd.data.id) || null;
          list.items.push(newItem);
          if (list.itemCount != null) list.itemCount++;
        }

        updatePickerItemCount(listId);
      });
    } else {
      var list = pickerLists.find(function(l) { return l.id === listId; });
      var item = list && Array.isArray(list.items) && list.items.find(function(i) {
        return i.shopifyProductGid === pickerGid;
      });

      if (!item) {
        checkbox.checked = true;
        checkbox.disabled = false;
        setItemLoading(checkbox, false);
        return;
      }

      /* Targeted removeItem if the item ID is known, else fall back to removeByProduct */
      var removeCall = item.id
        ? lib.removeItem(listId, item.id)
        : lib.removeByProduct(pickerGid);

      removeCall.then(function(result) {
        checkbox.disabled = false;
        setItemLoading(checkbox, false);
        if (!result.ok) { checkbox.checked = true; return; }

        if (item.id) {
          if (list && Array.isArray(list.items)) {
            list.items = list.items.filter(function(i) { return i.shopifyProductGid !== pickerGid; });
            if (list.itemCount != null && list.itemCount > 0) list.itemCount--;
          }
          updatePickerItemCount(listId);
        } else {
          /* removeByProduct clears the product from ALL lists — update every cache */
          pickerLists.forEach(function(l) {
            if (Array.isArray(l.items)) {
              var before = l.items.length;
              l.items = l.items.filter(function(i) { return i.shopifyProductGid !== pickerGid; });
              var removed = before - l.items.length;
              if (l.itemCount != null) l.itemCount = Math.max(0, l.itemCount - removed);
            }
          });
          renderPickerLists(pickerLists, pickerGid);
        }
      });
    }
  }

  function updatePickerItemCount(listId) {
    if (!pickerEl) return;
    var list = pickerLists.find(function(l) { return l.id === listId; });
    if (!list) return;
    var count = list.itemCount != null ? list.itemCount : (list.items ? list.items.length : 0);
    var checkbox = pickerEl.querySelector('input[data-list-id="' + listId + '"]');
    if (!checkbox) return;
    var nameEl = checkbox.parentNode.querySelector('.spectrum-wl-picker__name');
    if (nameEl) {
      nameEl.textContent = (list.name || 'Unnamed') + ' (' + count + ')';
    }
  }

  function handleCreateList() {
    var input = pickerEl.querySelector('.spectrum-wl-picker__input');
    var name = input.value.trim();
    if (!name) return;

    var lib = getLib();
    if (!lib) return;

    var btn = pickerEl.querySelector('.spectrum-wl-picker__create-btn');
    input.disabled = true;
    btn.disabled = true;

    lib.createWishlist(name).then(function(result) {
      input.disabled = false;
      btn.disabled = false;
      if (!result.ok) return;

      var newList = result.data;
      if (newList && newList.data) newList = newList.data;
      if (newList && newList.wishlist) newList = newList.wishlist;
      if (!newList || !newList.id) return;

      if (!newList.name) newList.name = name;
      if (!Array.isArray(newList.items)) newList.items = [];
      if (newList.itemCount == null) newList.itemCount = 0;

      pickerLists.push(newList);

      lib.addItem(newList.id, buildAddData(pickerGid, pickerVariantGid, pickerHandle, pickerTitle, pickerImage, pickerPrice)).then(function(addResult) {
        if (addResult.ok) {
          var newItem = { shopifyProductGid: pickerGid, shopifyVariantGid: pickerVariantGid, handle: pickerHandle };
          var rd = addResult.data;
          if (rd) newItem.id = rd.id || (rd.data && rd.data.id) || null;
          newList.items.push(newItem);
          newList.itemCount++;
        }
        renderPickerLists(pickerLists, pickerGid);
        input.value = '';
      });
    });
  }

  /* ── Click Handler (delegated) ─────────────────────────────────────── */

  document.addEventListener('click', function(e) {
    var wrapper = e.target.closest('[data-spectrum-wishlist-btn]');
    if (!wrapper) return;

    e.preventDefault();
    e.stopPropagation();

    var gid = wrapper.dataset.productGid;
    var variantGid = wrapper.dataset.variantGid || null;
    var handle = wrapper.dataset.productHandle || '';
    var title = wrapper.dataset.productTitle || '';
    var image = wrapper.dataset.productImage || '';
    var price = wrapper.dataset.productPrice;

    waitForLib(function(lib) {
      /* Logged-in: multi-list picker */
      if (isCustomerLoggedIn()) {
        openPicker(wrapper, lib, gid, variantGid, handle, title, image, price);
        return;
      }

      /* Guest: single-list optimistic toggle */
      if (_inflight[gid]) return;

      var isWishlisted = wrapper.classList.contains('is-wishlisted');
      _inflight[gid] = true;
      wrapper.classList.add('is-processing');
      wrapper.classList.toggle('is-wishlisted');

      var btnEl = wrapper.querySelector('button');
      if (btnEl) {
        btnEl.disabled = true;
        var newLabel = isWishlisted ? 'Add to wishlist' : 'Remove from wishlist';
        btnEl.setAttribute('aria-label', newLabel);
        btnEl.setAttribute('aria-pressed', String(!isWishlisted));
        btnEl.setAttribute('title', newLabel);
      }

      var apiCall = isWishlisted
        ? lib.removeByProduct(gid, { handle: handle, title: title })
        : lib.addItem('default', buildAddData(gid, variantGid, handle, title, image, price));
      var minDelay = new Promise(function(r) { setTimeout(r, 300); });

      Promise.all([apiCall, minDelay]).then(function(results) {
        var result = results[0];
        wrapper.classList.remove('is-processing');
        if (btnEl) btnEl.disabled = false;
        delete _inflight[gid];

        if (!result.ok) {
          /* Revert optimistic toggle */
          wrapper.classList.toggle('is-wishlisted');
          if (btnEl) {
            var revertLabel = isWishlisted ? 'Remove from wishlist' : 'Add to wishlist';
            btnEl.setAttribute('aria-label', revertLabel);
            btnEl.setAttribute('aria-pressed', String(isWishlisted));
            btnEl.setAttribute('title', revertLabel);
          }
        }
        /* SDK auto-emits wishlist:added / :removed via Spectrum.events */
      });
    }, 5);
  });

  /* ── Picker Close Events ────────────────────────────────────────────── */

  document.addEventListener('keydown', function(e) {
    if (e.key === 'Escape' && pickerEl && pickerEl.classList.contains('is-open')) {
      closePicker();
    }
  });

  document.addEventListener('click', function(e) {
    if (!pickerEl || !pickerEl.classList.contains('is-open')) return;
    if (e.target.closest('[data-spectrum-wishlist-btn]')) return;
    if (e.target.closest('.spectrum-wl-picker')) return;
    closePicker();
  });

  /* ── Cross-Component Sync (via Spectrum.events) ────────────────────── */

  var _syncTimer = null;

  function debouncedSync() {
    clearTimeout(_syncTimer);
    _syncTimer = setTimeout(function() {
      if (isCustomerLoggedIn()) {
        syncWishlistState();
      } else {
        updateHeaderCount(getGuestGids().length);
      }
    }, 50);
  }

  Spectrum.events.on('wishlist:added', function(event) {
    cachedWishlists = null;
    var gid = event.detail && event.detail.shopifyProductGid;
    if (gid) {
      document.querySelectorAll('[data-spectrum-wishlist-btn][data-product-gid="' + gid + '"]').forEach(function(el) {
        el.classList.add('is-wishlisted');
      });
    }
    debouncedSync();
  });

  Spectrum.events.on('wishlist:removed', function(event) {
    cachedWishlists = null;
    var gid = event.detail && event.detail.shopifyProductGid;
    if (gid) {
      document.querySelectorAll('[data-spectrum-wishlist-btn][data-product-gid="' + gid + '"]').forEach(function(el) {
        el.classList.remove('is-wishlisted');
      });
    }
    debouncedSync();
  });

  Spectrum.events.on('wishlist:merged', function() {
    cachedWishlists = null;
    syncWishlistState();
  });

  /* ── Init ─────────────────────────────────────────────────────────── */

  function init() {
    hydrateGuestState();
    syncWishlistState();
  }

  /* Re-sync on bfcache restore (back/forward navigation) */
  window.addEventListener('pageshow', function(e) {
    if (e.persisted) {
      cachedWishlists = null;
      if (isCustomerLoggedIn()) {
        syncWishlistState();
      } else {
        hydrateGuestState();
      }
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
